import { FC, useCallback, useState } from 'react';

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';

import MetadataTypeDefinitions from '@/shared/types/metadata/metadataTypeDefinitions.type';

import Package from '../../PackagingDetailsTableForm_v2/types/package.type';
import ProductTableRow from '../types/productTableRow.type';

type CustomColumnProps = {
  cellParams: GridRenderCellParams;
  columnName: string;
  packages?: Package[];
  handleUpdateTableRow: (value: {
    rowId: string;
    value: Partial<ProductTableRow>;
  }) => void;
};

const CustomColumn: FC<CustomColumnProps> = ({
  cellParams,
  columnName,
  packages,
  handleUpdateTableRow,
}) => {
  const row = cellParams.row as ProductTableRow;

  const [fields, setFields] = useState<
    Pick<
      ProductTableRow,
      | 'product_name'
      | 'country_of_origin'
      | 'unit_price'
      | 'weight'
      | 'product_description'
      | 'assign_package'
    >
  >({
    product_name: row.product_name || '',
    country_of_origin: row.country_of_origin || 'CA',
    unit_price: {
      currency: row.unit_price.currency || 'CAD',
      value: row.unit_price.value || '',
    },
    weight: {
      unit: (row.weight?.unit ||
        'in') as MetadataTypeDefinitions['weight']['unit'],
      value: row.weight?.value || 0,
    },
    product_description: row.product_description || '',
    assign_package: row.assign_package || packages?.[0].package_id || '',
  });

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setFields((prev) => ({ ...prev, [field]: value }));

      handleUpdateTableRow({
        rowId: row.fulfilment_queue_id,
        value: { [field]: value },
      });
    },
    [handleUpdateTableRow, row.fulfilment_queue_id],
  );

  switch (columnName) {
    case 'variant_id':
      return (
        <Typography variant='body2'>{row.variant_id.split('-')[0]}</Typography>
      );
    case 'product_name':
      return (
        <>
          <TextField
            fullWidth
            label=''
            placeholder='Provide product name'
            variant='standard'
            value={fields.product_name}
            onChange={(e) => handleFieldChange('product_name', e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </>
      );
    case 'origin':
      return (
        <Select
          variant='standard'
          value={fields.country_of_origin}
          onChange={(e) =>
            handleFieldChange('country_of_origin', e.target.value)
          }
        >
          <MenuItem value='CA'>Canada</MenuItem>
          <MenuItem value='USA'>USA</MenuItem>
        </Select>
      );
    case 'unit_price':
      return (
        <>
          <Stack direction='row' spacing={1}>
            <TextField
              label=''
              placeholder='Amount'
              variant='standard'
              value={fields.unit_price.value}
              inputProps={{ type: 'number' }}
              onChange={(e) =>
                handleFieldChange('unit_price', {
                  ...fields.unit_price,
                  value: e.target.value,
                })
              }
              onKeyDown={(e) => e.stopPropagation()}
            />

            <Select
              variant='standard'
              value={fields.unit_price.currency}
              onChange={(e) =>
                handleFieldChange('unit_price', {
                  ...fields.unit_price,
                  currency: e.target.value,
                })
              }
            >
              <MenuItem value='CAD'>CAD</MenuItem>
              <MenuItem value='USD'>USD</MenuItem>
            </Select>
          </Stack>
        </>
      );
    case 'weight':
      return (
        <Stack direction='row' spacing={1}>
          <TextField
            label=''
            placeholder='Weight'
            variant='standard'
            value={fields.weight.value}
            inputProps={{ type: 'number' }}
            onChange={(e) =>
              handleFieldChange('weight', {
                ...fields.weight,
                value: parseFloat(e.target.value),
              })
            }
            onKeyDown={(e) => e.stopPropagation()}
          />

          <Select
            variant='standard'
            value={fields.weight.unit}
            onChange={(e) =>
              handleFieldChange('weight', {
                ...fields.weight,
                unit: e.target.value,
              })
            }
          >
            <MenuItem value='lb'>lb</MenuItem>
            <MenuItem value='kg'>kg</MenuItem>
            <MenuItem value='g'>g</MenuItem>
            <MenuItem value='oz'>oz</MenuItem>
          </Select>
        </Stack>
      );
    case 'product_description':
      return (
        <TextField
          fullWidth
          label=''
          sx={{
            '& .MuiInputBase-input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          placeholder='Product Description'
          variant='standard'
          value={fields.product_description}
          onChange={(e) =>
            handleFieldChange('product_description', e.target.value)
          }
          onKeyDown={(e) => e.stopPropagation()}
        />
      );
    case 'assign_package':
      return (
        packages && (
          <Select
            variant='standard'
            value={fields.assign_package}
            onChange={(e) =>
              handleFieldChange('assign_package', e.target.value)
            }
          >
            {packages.map((item) => (
              <MenuItem key={item.package_id} value={item.package_id}>
                Package {item.package_number}
              </MenuItem>
            ))}
          </Select>
        )
      );
    default:
      return null;
  }
};

export default CustomColumn;
